import { z } from 'zod';
import {
  FindingSeveritySchema,
  InfraImpactSchema,
  InternalNetworkAdEntityKindSchema,
  SignalCategorySchema,
} from '@terra/shared-schemas-orchestrator';

export const InternalNetworkAttackPathEventKindSchema = z.enum([
  'HOST_DISCOVERED',
  'SERVICE_DISCOVERED',
  'WEAKNESS_EXPLOITED',
  'CREDENTIAL_OBTAINED',
  'DATA_ACCESSED',
  'IMPACT_ACHIEVED',
]);

export const InternalNetworkCredentialProvenanceSchema = z.enum([
  'DISCOVERED',
  'RELAYED',
  'CRACKED',
  'COMPROMISED',
]);

export const InternalNetworkCredentialPrivilegeTierSchema = z.enum([
  'UNKNOWN',
  'LOCAL_USER',
  'LOCAL_ADMIN',
  'DOMAIN_USER',
  'SERVICE_ACCOUNT',
  'DOMAIN_ADMIN',
  'ENTERPRISE_ADMIN',
]);

export const InternalNetworkAccessedResourceKindSchema = z.enum([
  'REPOSITORY',
  'FILE_SHARE',
  'DATABASE',
  'OBJECT_STORAGE',
  'MAILBOX',
  'BACKUP',
]);

export const InternalNetworkAttackPathEventStatusSchema = z.enum([
  'SUCCEEDED',
  'FAILED',
  'IN_PROGRESS',
]);

const AttackPathEventBaseSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  hopIndex: z.number().int().nullable(),
  occurredAt: z.iso.datetime(),
  status: InternalNetworkAttackPathEventStatusSchema,
  sourceEventIds: z.array(z.string()),
  operationId: z.string().nullable(),
  operationTaskId: z.string().nullable(),
  evidenceId: z.string().nullable(),
  mitreTechniqueId: z.string().nullable(),
  technique: z.string().nullable(),
  exploitsWeakness: z.boolean(),
});

export const InternalNetworkHostDiscoveredEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('HOST_DISCOVERED'),
    hostname: z.string(),
    assetId: z.string().nullable(),
    adEntityId: z.string().nullable(),
    isDomainController: z.boolean(),
    isCrownJewel: z.boolean(),
    operatingSystem: z.string().nullable(),
  });

export const InternalNetworkServiceDiscoveredEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('SERVICE_DISCOVERED'),
    hostname: z.string(),
    assetId: z.string().nullable(),
    serviceName: z.string(),
    protocol: z.string(),
    port: z.number().int(),
    product: z.string().nullable(),
    version: z.string().nullable(),
  });

export const InternalNetworkWeaknessExploitedEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('WEAKNESS_EXPLOITED'),
    weaknessId: z.string(),
    title: z.string(),
    findingId: z.string().nullable(),
    category: SignalCategorySchema.nullable(),
    severity: FindingSeveritySchema.nullable(),
    hostname: z.string().nullable(),
    sourceHostname: z.string().nullable(),
    adEntityId: z.string().nullable().optional(),
    isProven: z.boolean(),
    retryCount: z.number().int().nonnegative().optional(),
  });

export const InternalNetworkCredentialObtainedEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('CREDENTIAL_OBTAINED'),
    label: z.string(),
    provenance: InternalNetworkCredentialProvenanceSchema,
    privilegeTier: InternalNetworkCredentialPrivilegeTierSchema,
    username: z.string().nullable(),
    domain: z.string().nullable(),
    credentialId: z.string().nullable(),
    hostname: z.string().nullable(),
  });

export const InternalNetworkDataAccessedEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('DATA_ACCESSED'),
    resourceKind: InternalNetworkAccessedResourceKindSchema,
    resourceName: z.string(),
    hostname: z.string().nullable(),
    recordCount: z.number().int().nullable(),
    sizeBytes: z.number().int().nullable(),
  });

export const InternalNetworkImpactAchievedEventSchema =
  AttackPathEventBaseSchema.extend({
    kind: z.literal('IMPACT_ACHIEVED'),
    infraImpact: InfraImpactSchema,
    findingId: z.string().nullable(),
    hostname: z.string().nullable(),
    sourceHostname: z.string().nullable(),
  });

export const InternalNetworkAttackPathEventSchema = z.discriminatedUnion(
  'kind',
  [
    InternalNetworkHostDiscoveredEventSchema,
    InternalNetworkServiceDiscoveredEventSchema,
    InternalNetworkWeaknessExploitedEventSchema,
    InternalNetworkCredentialObtainedEventSchema,
    InternalNetworkDataAccessedEventSchema,
    InternalNetworkImpactAchievedEventSchema,
  ],
);

export const InternalNetworkAttackPathTimelineSchema = z.object({
  assessmentId: z.string().nullable(),
  networkId: z.string(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  events: z.array(InternalNetworkAttackPathEventSchema),
  origin: z.enum(['RECORDED', 'DERIVED']),
});

export const InternalNetworkAssessmentSeedSchema = z.object({
  id: z.string(),
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string(),
  label: z.string().nullable(),
});

export const InternalNetworkV2AchievementsSchema = z.object({
  entitiesObtained: z.array(z.string()),
  credentialsObtained: z.array(z.string()),
  hostsReached: z.array(z.string()),
  servicesDiscovered: z.array(z.string()),
  impactsAchieved: z.array(z.string()),
});

export const RecordInternalNetworkAttackPathEventBodySchema =
  z.discriminatedUnion('kind', [
    InternalNetworkHostDiscoveredEventSchema.omit({
      id: true,
      networkId: true,
    }),
    InternalNetworkServiceDiscoveredEventSchema.omit({
      id: true,
      networkId: true,
    }),
    InternalNetworkWeaknessExploitedEventSchema.omit({
      id: true,
      networkId: true,
    }),
    InternalNetworkCredentialObtainedEventSchema.omit({
      id: true,
      networkId: true,
    }),
    InternalNetworkDataAccessedEventSchema.omit({
      id: true,
      networkId: true,
    }),
    InternalNetworkImpactAchievedEventSchema.omit({
      id: true,
      networkId: true,
    }),
  ]);

export const INTERNAL_NETWORK_ATTACK_PATH_EVENT_KINDS =
  InternalNetworkAttackPathEventKindSchema.options;
export const INTERNAL_NETWORK_CREDENTIAL_PROVENANCE_VALUES =
  InternalNetworkCredentialProvenanceSchema.options;
export const INTERNAL_NETWORK_CREDENTIAL_PRIVILEGE_TIER_VALUES =
  InternalNetworkCredentialPrivilegeTierSchema.options;

export type InternalNetworkAttackPathEventKind = z.infer<
  typeof InternalNetworkAttackPathEventKindSchema
>;
export type InternalNetworkCredentialProvenance = z.infer<
  typeof InternalNetworkCredentialProvenanceSchema
>;
export type InternalNetworkCredentialPrivilegeTier = z.infer<
  typeof InternalNetworkCredentialPrivilegeTierSchema
>;
export type InternalNetworkAccessedResourceKind = z.infer<
  typeof InternalNetworkAccessedResourceKindSchema
>;
export type InternalNetworkAttackPathEventStatus = z.infer<
  typeof InternalNetworkAttackPathEventStatusSchema
>;
export type InternalNetworkHostDiscoveredEventDto = z.infer<
  typeof InternalNetworkHostDiscoveredEventSchema
>;
export type InternalNetworkServiceDiscoveredEventDto = z.infer<
  typeof InternalNetworkServiceDiscoveredEventSchema
>;
export type InternalNetworkWeaknessExploitedEventDto = z.infer<
  typeof InternalNetworkWeaknessExploitedEventSchema
>;
export type InternalNetworkCredentialObtainedEventDto = z.infer<
  typeof InternalNetworkCredentialObtainedEventSchema
>;
export type InternalNetworkDataAccessedEventDto = z.infer<
  typeof InternalNetworkDataAccessedEventSchema
>;
export type InternalNetworkImpactAchievedEventDto = z.infer<
  typeof InternalNetworkImpactAchievedEventSchema
>;
export type InternalNetworkAttackPathEventDto = z.infer<
  typeof InternalNetworkAttackPathEventSchema
>;
export type InternalNetworkAttackPathTimelineDto = z.infer<
  typeof InternalNetworkAttackPathTimelineSchema
>;
export type InternalNetworkAssessmentSeedDto = z.infer<
  typeof InternalNetworkAssessmentSeedSchema
>;
export type InternalNetworkV2AchievementsDto = z.infer<
  typeof InternalNetworkV2AchievementsSchema
>;
export type RecordInternalNetworkAttackPathEventBodyDto = z.infer<
  typeof RecordInternalNetworkAttackPathEventBodySchema
>;
